import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";

import { SpecialUnitsRulesFragment } from "../fragments/specialUnits";
import { upperCase } from "lodash";
import { SpecialUnitsListType } from "./enums";

export async function getCommissionCyclesSpecialUnitsList({ locationId }: { locationId: string }) {
	const query = gql`
		query getCommissionCyclesSpecialUnits($locationId: String!) {
			getCommissionCyclesSpecialUnits(locationId: $locationId) {
				_id
				title
				startDate
				status
				endDate
				specialUnitsList {
					specialUnitsListId {
						_id
						title
						effectiveDateFrom
						effectiveDateTo
						createdBy {
							fullName
						}
						createdAt
						lastModifiedBy {
							fullName
						}
						lastModifiedOn
						unitsCount
						specialUnitsListType
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getCommissionCyclesSpecialUnits;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getCommissionCyclesSpecialUnitsList",
			message: "Error getting commission cycles special units list",
			variables,
		});
		return null;
	}
}

export async function getSpecialUnitListById(args: { locationId: string; listId: string; specialUnitsListType: string }) {
	const normalizedSpecialUnitsListType = upperCase(args.specialUnitsListType);

	switch (normalizedSpecialUnitsListType) {
		case SpecialUnitsListType.MANAGED:
			return getSpecialUnitListByIdManaged(args);
		case SpecialUnitsListType.MANUAL:
			return getSpecialUnitListByIdManual(args);
	}

	return null;
}
function generateCommonSpecialUnitsQuery(unitsFragment: string) {
	return gql`
        query getSpecialUnitsListById($locationId: String!, $_id: String!, $specialUnitsListType: String!) {
            getSpecialUnitsListById(locationId: $locationId, _id: $_id, specialUnitsListType: $specialUnitsListType) {
                ${unitsFragment}
                _id
				createdBy {
				fullName
				}
				createdAt
				lastModifiedBy {
				fullName
				}
				lastModifiedOn
				title
				unitsCount
				effectiveDateFrom
				effectiveDateTo
                rules {
                   ...SpecialUnitsRules 
                }
			}
		}
		${SpecialUnitsRulesFragment}
    `;
}

export async function getSpecialUnitListByIdManual(args: { locationId: string; listId: string; specialUnitsListType: string }) {
	const { locationId, listId, specialUnitsListType } = args;
	const unitsFragment = `
        units {
			_id
            commission {
                input
                value
            }
            createdBy {
                fullName
            }
			createdAt
           	lastModifiedBy {
                fullName
            }
			lastModifiedOn
			description
            stockNumber
            status
            saleDate
        }
    `;
	const query = generateCommonSpecialUnitsQuery(unitsFragment);

	const variables = {
		locationId,
		specialUnitsListType,
		_id: listId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSpecialUnitsListById;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getSpecialUnitListByIdManual",
			message: "Error getting special unit list by id",
			variables,
		});
		return null;
	}
}

export async function getSpecialUnitListByIdManaged(args = { locationId: "", listId: "", specialUnitsListType: "" }) {
	const { locationId, listId, specialUnitsListType } = args;
	const unitsFragment = `
		units {
            _id
            hierarchy
            isParent
			search 
			criteria
            inventoryList {
                _id
                hierarchy
                isParent
                inventory {
                    _id
                    createdAt
                    locationId
                    averageAge
                    status
					statusHistory {
						_id
						status
						stockId
						locationId
						date
					}
                    properties {
                        displayName
                        dataType
                        value
                    }
                    updatedAt
                }
            }
            commission {
                input
                value
            }
            createdBy {
                fullName
            }
            createdAt
            lastModifiedOn
        }
    `;
	const query = generateCommonSpecialUnitsQuery(unitsFragment);

	const variables = {
		locationId,
		specialUnitsListType,
		_id: listId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSpecialUnitsListById;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getSpecialUnitListByIdManaged",
			message: "Error getting special unit list by id",
			variables,
		});
		return null;
	}
}

export async function getCurrentSalesPerformanceFormatVersion(locationId = "") {
	const query = gql`
		query getCurrentSalesPerformanceReportVersion($locationId: String!) {
			getCurrentSalesPerformanceReportVersion(locationId: $locationId) {
				_id
				locationId
				effectiveDateTime
				frontProducts {
					_id
					label
					reportField
					contractUnits
				}
				backProducts {
					_id
					label
					reportField
					contractUnits
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getCurrentSalesPerformanceReportVersion;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getCurrentSalesPerformanceFormatVersion",
			message: "Error getting current sales performance format version",
			variables,
		});
		return null;
	}
}

export async function getSpecialUnits(locationId: string, commissionCycleId: string, inputFilter: { employeeId: string }) {
	const query = gql`
		query getSpecialUnits($locationId: String!, $commissionCycleId: String, $filter: SpecialUnitsFilterInput) {
			getSpecialUnits(locationId: $locationId, commissionCycleId: $commissionCycleId, filter: $filter) {
				_id
				saleDate
				dealNumber
				stockNumber
				customerName
				description
				commission
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		filter: inputFilter,
	};

	try {
		const { data } = await apiInstance.post("/graphql", { query, variables });
		return data.data?.getSpecialUnits;
	} catch (err) {
		errorLogger(err as Error, { function: "getSpecialUnits", message: "Error getting special units", variables });
		return null;
	}
}

export interface ISearchReynoldsInventoryResponse {
	_id: string;
	make: string;
	model: string;
	year: string;
	trim: string;
	status: string;
	stockId: string;
}

/******************************************* Reynolds Related *******************************************/
export async function searchReynoldsInventory(locationId: string, searchCriteria: string) {
	const query = gql`
		query searchReynoldsInventory($locationId: String!, $searchCriteria: String) {
			searchReynoldsInventory(locationId: $locationId, searchCriteria: $searchCriteria) {
				_id
				make
				model
				year
				trim
				status
				stockId
			}
		}
	`;

	const variables = {
		locationId,
		searchCriteria,
	};

	try {
		const response: GraphqlQueryResponse<"searchReynoldsInventory", ISearchReynoldsInventoryResponse[]> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.searchReynoldsInventory;
	} catch (err) {
		errorLogger(err as Error, { function: "searchReynoldsInventory", message: "Error searching Reynolds inventory", variables });
		return null;
	}
}

export async function getInventoryStatusList(provider: string) {
	const query = gql`
		query getInventoryStatusList($provider: String!) {
			getInventoryStatusList(provider: $provider) {
				_id
				provider
				statusesList {
					_id
					title
					statuses {
						_id
						isEditable
						status
					}
				}
			}
		}
	`;

	const variables = {
		provider,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });

		return response.data.data?.getInventoryStatusList;
	} catch (err) {
		errorLogger(err as Error, { function: "getInventoryStatusList", message: "Error getting inventory status list", variables });
		return null;
	}
}

export async function getInventoryFields(locationId: string, filter?: string) {
	const query = gql`
		query getInventoryFields($locationId: String, $filter: String) {
			getInventoryFields(locationId: $locationId, filter: $filter) {
				_id
				locationId
				provider
				values
			}
		}
	`;

	const variables = { filter, locationId };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getInventoryFields;
	} catch (err) {
		errorLogger(err as Error, { function: "getInventoryFields", message: "Error getting inventory fields", variables });
		return null;
	}
}

export async function getFormatVersionProducts(locationId: string) {
	const query = gql`
		query getFormatVersionProducts($locationId: String!) {
			getFormatVersionProducts(locationId: $locationId) {
				_id
				frontProducts {
					_id
					label
					reportField
					contractUnits
				}
				backProducts {
					_id
					label
					reportField
					contractUnits
				}
			}
		}
	`;

	const variables = { locationId };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getFormatVersionProducts;
	} catch (err) {
		errorLogger(err as Error, { function: "getFormatVersionProducts", message: "Error getting FormatVersionProducts", variables });
		return null;
	}
}
